import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Send } from "lucide-react";
import { usePageTitle } from "@/hooks/usePageTitle";

const RIPPLE_LIGHT = "https://customer-assets.emergentagent.com/job_jci-content-platform/artifacts/xmrja44a_Ripples.png";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  usePageTitle("Contact");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Failed to send message. Please try again.");
    }
    setSending(false);
  };

  return (
    <div data-testid="contact-page">
      <div className="bg-jci-black py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">Contact Us</h1>
          <p className="text-white/50 mt-3 text-base">Get in touch with JCI Bangladesh</p>
        </div>
        <div className="h-1 bg-gradient-to-r from-jci-blue via-jci-teal to-jci-navy mt-8" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-16 relative overflow-hidden">
        <img src={RIPPLE_LIGHT} alt="" className="ripple-bg -right-40 top-0 w-[500px]" />
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 relative z-10">
          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-6">
            <h2 className="text-2xl font-bold text-jci-black mb-6">Let's Connect</h2>
            {[
              { icon: MapPin, label: "Address", value: "House 11A, Road 113, Gulshan, Dhaka" },
              { icon: Phone, label: "Phone", value: "+8801843632811" },
              { icon: Mail, label: "Email", value: "bangladesh@jci.cc" },
            ].map((item, i) => (
              <Card key={i} className="border-0 shadow-sm bg-slate-50">
                <CardContent className="p-4 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-jci-blue/10 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-jci-blue" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{item.label}</p>
                    <p className="text-sm font-medium text-jci-black">{item.value}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
            {/* Google Map */}
            <div className="rounded-xl overflow-hidden shadow-lg mt-4" data-testid="google-map">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3651.0!2d90.4141!3d23.7929!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3755c77726e4e0e5%3A0x3f5c8e8c8f8c8f8c!2s11a%20Rd%20113%2C%20Dhaka%201212!5e0!3m2!1sen!2sbd!4v1707660000000!5m2!1sen!2sbd"
                width="100%"
                height="280"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="JCI Bangladesh Location"
              />
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3">
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-jci-black mb-6">Send a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="name" className="text-sm font-medium text-slate-700">Name *</Label>
                      <Input id="name" data-testid="contact-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Your name" className="mt-1.5" />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-sm font-medium text-slate-700">Email *</Label>
                      <Input id="email" type="email" data-testid="contact-email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="your@email.com" className="mt-1.5" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="subject" className="text-sm font-medium text-slate-700">Subject</Label>
                    <Input id="subject" data-testid="contact-subject" value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} placeholder="What is this about?" className="mt-1.5" />
                  </div>
                  <div>
                    <Label htmlFor="message" className="text-sm font-medium text-slate-700">Message *</Label>
                    <Textarea id="message" data-testid="contact-message" value={form.message} onChange={e => setForm({...form, message: e.target.value})} placeholder="Your message..." rows={5} className="mt-1.5" />
                  </div>
                  <Button type="submit" data-testid="contact-submit" disabled={sending} className="bg-jci-blue hover:bg-jci-navy text-white w-full sm:w-auto px-8">
                    {sending ? "Sending..." : "Send Message"} <Send className="w-4 h-4 ml-2" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
